import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { loadFeatured } from '../../utils/featuredListings';
import { ENERGY_TYPES } from '../EnergySymbols/EnergySymbols';
import './SearchBox.css';

const API = process.env.REACT_APP_API_BASE_URL;
const DEBOUNCE_MS = 250;
const MAX_RESULTS = 10;
const SUGGESTED_COUNT = 8;

// Category pills shown when the box is focused and empty. Each one browses the
// listings page by supertype/subtype, the same links as the side menu.
const CATEGORIES = [
    { label: 'Pokémon', to: '/pokemon' },
    { label: 'Supporters', to: '/listings?supertype=trainer&subtype=Supporter' },
    { label: 'Stadiums', to: '/listings?supertype=trainer&subtype=Stadium' },
    { label: 'Tools', to: `/listings?supertype=trainer&subtype=${encodeURIComponent('Pokémon Tool')}` },
    { label: 'Items', to: '/listings?supertype=trainer&subtype=Item' },
    { label: 'Special Energies', to: '/listings?supertype=energy&subtype=Special' },
];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Wraps every case-insensitive occurrence of `query` in <strong>.
const Highlight = ({ text, query }) => {
    const q = query.trim();
    if (!q) return text;
    const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
    return parts.map((part, i) =>
        part.toLowerCase() === q.toLowerCase()
            ? <strong key={i}>{part}</strong>
            : <React.Fragment key={i}>{part}</React.Fragment>
    );
};

const cardName = (l) => l.cert?.card?.name || l.cert?.name || `Cert ${l.cert?.cert_number}`;

const gradeDigits = (grade) => String(grade || '').match(/\d+(\.\d+)?/)?.[0] || null;

// Cards whose name starts with the query first, then the rest; pricier first within each.
const rankResults = (listings, q) => {
    const lower = q.toLowerCase();
    return [...listings].sort((a, b) => {
        const aStarts = cardName(a).toLowerCase().startsWith(lower) ? 0 : 1;
        const bStarts = cardName(b).toLowerCase().startsWith(lower) ? 0 : 1;
        return (aStarts - bStarts) || ((Number(b.price) || 0) - (Number(a.price) || 0));
    });
};

// One listing row: cert image on the left, name (with the match bolded), then
// set, grade and price underneath.
const ListingRow = ({ listing, query, onPick }) => {
    const cert = listing.cert || {};
    const img = cert.cert_images?.front_image_url;
    const grade = gradeDigits(cert.grade);
    const setName = cert.card?.set?.name || cert.set;
    const price = Number(listing.price);
    const sub = [setName, grade && `PSA ${grade}`, Number.isFinite(price) && `$${price.toFixed(0)}`]
        .filter(Boolean)
        .join(' · ');
    return (
        <li>
            <Link to={`/listing/${cert.cert_number}`} className="gh-suggest-row" onClick={onPick}>
                <span className="gh-suggest-thumb">
                    {img ? <img src={img} alt="" loading="lazy" /> : null}
                </span>
                <span className="gh-suggest-text">
                    <span className="gh-suggest-name"><Highlight text={cardName(listing)} query={query} /></span>
                    {sub && <span className="gh-suggest-sub">{sub}</span>}
                </span>
            </Link>
        </li>
    );
};

// Search input with a suggestion dropdown. Empty + focused: energy-type icons,
// category pills and suggested (featured) listings. Typing: live card-name results, fetched 250ms
// after the last keystroke, with the matched text bolded. Enter or the search
// button still goes to the full results page.
// `variant` picks the look: "hero" (homepage) or "header" (compact).
const SearchBox = ({ variant, placeholder, source, iconSize = 18 }) => {
    const navigate = useNavigate();
    const posthog = usePostHog();
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const listboxId = useId();

    const [term, setTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [featured, setFeatured] = useState(null);
    const [results, setResults] = useState({ query: '', listings: [] });

    const query = term.trim();

    // Suggested listings: fetched once, the first time the box opens.
    useEffect(() => {
        if (!open || featured) return;
        let cancelled = false;
        loadFeatured(SUGGESTED_COUNT).then((list) => { if (!cancelled) setFeatured(list); });
        return () => { cancelled = true; };
    }, [open, featured]);

    // Live results, debounced; a newer keystroke cancels the older request.
    useEffect(() => {
        if (!query) {
            setResults({ query: '', listings: [] });
            return undefined;
        }
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            try {
                const p = new URLSearchParams({ cardName: query, count: '50' });
                const res = await fetch(`${API}/listings?${p}`, { signal: controller.signal });
                const data = res.ok ? await res.json() : { listings: [] };
                setResults({ query, listings: rankResults(data.listings || [], query).slice(0, MAX_RESULTS) });
            } catch (e) {
                if (e.name === 'AbortError') return;
                setResults({ query, listings: [] });
            }
        }, DEBOUNCE_MS);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [query]);

    // Close when a click lands anywhere outside the box.
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
        };
    }, [open]);

    const close = () => { setOpen(false); inputRef.current?.blur(); };

    const submit = () => {
        if (!query) return;
        try { posthog.capture('search_submitted', { search_term: query, source }); } catch { /* optional */ }
        close();
        navigate(`/listings/${encodeURIComponent(query)}`);
    };

    const pick = (kind) => () => {
        try { posthog.capture('search_suggestion_clicked', { kind, search_term: query, source }); } catch { /* optional */ }
        close();
    };

    // While the next search is in flight, keep showing the previous results
    // rather than flashing a loading message on every keystroke.
    const showingResults = !!query;
    const resultsFresh = results.query === query;
    const body = useMemo(() => {
        if (showingResults) {
            return (
                <>
                    {results.listings.length === 0 ? (
                        <p className="gh-suggest-msg">
                            {resultsFresh ? `No cards named “${query}” right now.` : 'Searching…'}
                        </p>
                    ) : (
                        <ul className="gh-suggest-list">
                            {results.listings.map((l) => (
                                <ListingRow key={l.id || l.cert?.cert_number} listing={l} query={query} onPick={pick('result')} />
                            ))}
                        </ul>
                    )}
                    <Link
                        to={`/listings/${encodeURIComponent(query)}`}
                        className="gh-suggest-all"
                        onClick={pick('see_all')}
                    >
                        See all “{query}” cards
                    </Link>
                </>
            );
        }
        return (
            <>
                <h3 className="gh-suggest-heading">Types</h3>
                <div className="gh-suggest-types">
                    {ENERGY_TYPES.map((t) => (
                        <Link
                            key={t.name}
                            to={`/listings?type=${encodeURIComponent(t.name)}`}
                            className="gh-suggest-type"
                            title={t.name}
                            aria-label={`${t.name} type`}
                            onClick={pick(`type:${t.name}`)}
                        >
                            <img src={t.icon} alt="" />
                        </Link>
                    ))}
                </div>
                <h3 className="gh-suggest-heading">Categories</h3>
                <div className="gh-suggest-pills">
                    {CATEGORIES.map((c) => (
                        <Link key={c.label} to={c.to} className="gh-suggest-pill" onClick={pick(`category:${c.label}`)}>
                            {c.label}
                        </Link>
                    ))}
                </div>
                {featured && featured.length > 0 && (
                    <>
                        <h3 className="gh-suggest-heading">Suggested</h3>
                        <ul className="gh-suggest-list">
                            {featured.map((l) => (
                                <ListingRow key={l.id || l.cert?.cert_number} listing={l} query="" onPick={pick('suggested')} />
                            ))}
                        </ul>
                    </>
                )}
            </>
        );
    }, [showingResults, resultsFresh, results, query, featured]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div ref={wrapRef} className={`gh-searchbox gh-searchbox-${variant}${open ? ' is-open' : ''}`}>
            <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={term}
                onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') submit();
                    if (e.key === 'Escape') close();
                }}
                role="combobox"
                aria-label="Search cards"
                aria-controls={listboxId}
                aria-expanded={open}
                aria-autocomplete="list"
                autoComplete="off"
            />
            <button type="button" className="gh-searchbox-submit" onClick={submit} aria-label="Search">
                <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                    <circle cx="11" cy="11" r="7" />
                    <line x1="16.5" y1="16.5" x2="21" y2="21" />
                </svg>
            </button>

            {open && <div id={listboxId} className="gh-suggest" role="listbox">{body}</div>}
        </div>
    );
};

export default SearchBox;
